import { useMemo } from 'react';
import { Input } from '@/components/ui/Input';
import { KeyValueFieldsEditor } from '@/components/ui/KeyValueFieldsEditor';
import {
  keyValuePairsFromJson,
  keyValuePairsToJson,
} from '@/utils/keyValueJson';

type Props = {
  actionType: string;
  value: string;
  onChange: (json: string) => void;
};

const PROJECT_ACTION_FIELDS: Record<string, { value: string; label: string; placeholder?: string }[]> = {
  NOTIFY: [
    { value: 'message', label: 'Message', placeholder: 'Notification text' },
    { value: 'channel', label: 'Channel', placeholder: 'email or in-app' },
  ],
  ASSIGN_TASK: [
    { value: 'assigneeId', label: 'Assignee ID', placeholder: 'Employee or user ID' },
    { value: 'taskTitle', label: 'Task title', placeholder: 'Optional title' },
  ],
  UPDATE_STATUS: [{ value: 'status', label: 'Status', placeholder: 'e.g. IN_PROGRESS' }],
  CREATE_ISSUE: [
    { value: 'title', label: 'Issue title', placeholder: 'Summary' },
    { value: 'description', label: 'Description', placeholder: 'Details' },
  ],
};

export function ActionConfigBuilder({ actionType, value, onChange }: Props) {
  const pairs = useMemo(() => keyValuePairsFromJson(value), [value]);
  const fieldOptions = PROJECT_ACTION_FIELDS[actionType];

  if (fieldOptions) {
    return (
      <KeyValueFieldsEditor
        label="Action settings"
        hint="Configure what happens when this automation runs."
        pairs={pairs}
        onChange={(next) => onChange(keyValuePairsToJson(next))}
        fieldOptions={fieldOptions}
        addLabel="Add setting"
        allowCustomField
      />
    );
  }

  return (
    <KeyValueFieldsEditor
      label="Action settings"
      pairs={pairs}
      onChange={(next) => onChange(keyValuePairsToJson(next))}
      addLabel="Add setting"
    />
  );
}

/** POS / payment gateway credential fields per provider. */
export function GatewayConfigBuilder({
  provider,
  value,
  onChange,
}: {
  provider: string;
  value: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  const fields = useMemo(() => {
    switch (provider) {
      case 'RAZORPAY':
        return [
          { key: 'keyId', label: 'Key ID', secret: false },
          { key: 'keySecret', label: 'Key secret', secret: true },
        ];
      case 'STRIPE':
      case 'STRIPE_TERMINAL':
        return [
          { key: 'publishableKey', label: 'Publishable key', secret: false },
          { key: 'secretKey', label: 'Secret key', secret: true },
        ];
      case 'PAYTM':
      case 'CASHFREE':
      case 'PHONEPE':
      case 'PAYU':
        return [
          { key: 'merchantId', label: 'Merchant ID', secret: false },
          { key: 'key', label: 'API key', secret: false },
          { key: 'secret', label: 'API secret', secret: true },
        ];
      default:
        return [
          { key: 'keyId', label: 'Client / key ID', secret: false },
          { key: 'keySecret', label: 'Secret', secret: true },
          { key: 'merchantId', label: 'Merchant ID (optional)', secret: false },
        ];
    }
  }, [provider]);

  return (
    <div className="space-y-3">
      <p className="label">Gateway credentials</p>
      {fields.map((f) => (
        <Input
          key={f.key}
          label={f.label}
          type={f.secret ? 'password' : 'text'}
          value={value[f.key] ?? ''}
          onChange={(e) => onChange({ ...value, [f.key]: e.target.value })}
        />
      ))}
    </div>
  );
}
